// Cash Cascade — table layout, scoring constants, and dependency-free
// physics math (no React, no canvas). Kept pure so it's directly testable,
// matching the pattern every other level's data.js follows.

export const TABLE_WIDTH = 400;
export const TABLE_HEIGHT = 700;
export const TABLE_CENTER = { x: TABLE_WIDTH / 2, y: TABLE_HEIGHT / 2 };

export const BALL_RADIUS = 8;
export const GRAVITY = 900; // px/s^2
export const WALL_RESTITUTION = 0.85;
export const BUMPER_KICK = 260; // px/s impulse added along the collision normal
export const FLIPPER_KICK = 650; // px/s impulse added when an active flipper is hit
// Flippers are modeled as a pivot-to-tip line for drawing, but collide as a
// capsule with this width — a zero-width line collider is too thin for the
// ball's per-frame movement at speed, causing it to tunnel through without
// registering a hit.
export const FLIPPER_WIDTH = 18;
// A fully elastic (restitution 1) bounce off an *inactive* flipper can trap
// the ball in a perpetual near-zero-energy micro-bounce right at the
// collision boundary — it never drains, but never really moves either.
// Bouncing off a resting flipper loses some energy instead, same as a real
// idle paddle would, so repeated contact settles rather than sustaining.
export const FLIPPER_REST_RESTITUTION = 0.55;

// Safety net: if the ball is near the bottom of the table and essentially
// motionless for this long, treat it as lost rather than risk a rare
// physics edge case (a stable resting contact) stalling the game forever.
export const STALL_SPEED_THRESHOLD = 30; // px/s
export const STALL_TIMEOUT_MS = 1500;
export const STALL_ZONE_Y = 600; // only watches the lower part of the table

export const BALLS_PER_GAME = 3;

export const BUMPER_POINTS = 100;
export const TARGET_POINTS = 250;
export const JACKPOT_BONUS = 1000;

export const SCORE_TO_XP_DIVISOR = 30;
export const MAX_XP_AWARD = 900;

// Outer boundary as a sequence of wall segments, walked clockwise from the
// bottom of the left wall. There is a deliberate gap between the last two
// segments (the funnel walls) — that gap is the drain, see DRAIN_GAP below.
export const WALLS = [
  { x1: 20, y1: 650, x2: 20, y2: 60 }, // left wall
  { x1: 20, y1: 60, x2: 60, y2: 20 }, // top-left corner
  { x1: 60, y1: 20, x2: 340, y2: 20 }, // top wall
  { x1: 340, y1: 20, x2: 380, y2: 60 }, // top-right corner
  { x1: 380, y1: 60, x2: 380, y2: 650 }, // right wall (doubles as the plunger lane wall)
  { x1: 380, y1: 650, x2: 240, y2: 690 }, // right funnel toward the drain
  { x1: 160, y1: 690, x2: 20, y2: 650 }, // left funnel toward the drain
];

export const BUMPERS = [
  { id: "b1", x: 140, y: 220, radius: 22 },
  { id: "b2", x: 260, y: 220, radius: 22 },
  { id: "b3", x: 200, y: 320, radius: 24 },
  { id: "b4", x: 90, y: 380, radius: 20 },
];

export const TARGETS = [
  { id: "t1", x: 60, y: 160, radius: 12 },
  { id: "t2", x: 200, y: 100, radius: 12 },
  { id: "t3", x: 340, y: 160, radius: 12 },
];

export const LEFT_FLIPPER = {
  pivot: { x: 150, y: 660 },
  length: 70,
  restAngle: 0.6,
  activeAngle: -0.5,
  angularSpeed: 14, // rad/s
};

export const RIGHT_FLIPPER = {
  pivot: { x: 250, y: 660 },
  length: 70,
  restAngle: Math.PI - 0.6,
  activeAngle: Math.PI + 0.5,
  angularSpeed: 14,
};

export const DRAIN_GAP = { xMin: 160, xMax: 240, y: 690 };

export const LAUNCH_POSITION = { x: 362, y: 630 };
export const LAUNCH_VELOCITY = { x: -60, y: -620 };

// Returns the unit normal of a wall segment, always pointing toward the
// table's center — so callers never have to hand-verify which way a
// hardcoded normal points for a given segment's winding direction.
export function wallNormal(segment) {
  const dx = segment.x2 - segment.x1;
  const dy = segment.y2 - segment.y1;
  const len = Math.hypot(dx, dy) || 1;
  let nx = -dy / len;
  let ny = dx / len;
  const mx = (segment.x1 + segment.x2) / 2;
  const my = (segment.y1 + segment.y2) / 2;
  const toCenterX = TABLE_CENTER.x - mx;
  const toCenterY = TABLE_CENTER.y - my;
  if (nx * toCenterX + ny * toCenterY < 0) {
    nx = -nx;
    ny = -ny;
  }
  return { x: nx, y: ny };
}

export function closestPointOnSegment(p, a, b) {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const abLenSq = abx * abx + aby * aby;
  let t = abLenSq === 0 ? 0 : ((p.x - a.x) * abx + (p.y - a.y) * aby) / abLenSq;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + abx * t, y: a.y + aby * t };
}

export function reflect(velocity, normal, restitution = 1) {
  const dot = velocity.x * normal.x + velocity.y * normal.y;
  return {
    x: (velocity.x - 2 * dot * normal.x) * restitution,
    y: (velocity.y - 2 * dot * normal.y) * restitution,
  };
}

export function circleCircleCollision(centerA, radiusA, centerB, radiusB) {
  const dx = centerB.x - centerA.x;
  const dy = centerB.y - centerA.y;
  const distance = Math.hypot(dx, dy);
  const overlap = radiusA + radiusB - distance;
  return overlap > 0 ? { overlap, distance, dx, dy } : null;
}

export function resolveBumperHit(ballPos, ballVel, bumperPos, kickStrength, restitution = 1) {
  const dx = ballPos.x - bumperPos.x;
  const dy = ballPos.y - bumperPos.y;
  const distance = Math.hypot(dx, dy) || 1;
  const normal = { x: dx / distance, y: dy / distance };
  const reflected = reflect(ballVel, normal, restitution);
  return {
    x: reflected.x + normal.x * kickStrength,
    y: reflected.y + normal.y * kickStrength,
  };
}

export function isBallInDrain(ballPos) {
  return ballPos.y > DRAIN_GAP.y && ballPos.x > DRAIN_GAP.xMin && ballPos.x < DRAIN_GAP.xMax;
}

export function xpFromScore(totalScore) {
  return Math.min(MAX_XP_AWARD, Math.round(totalScore / SCORE_TO_XP_DIVISOR));
}
